import {
  IntercomProvider as LibIntercomProvider,
  useIntercom,
} from 'react-use-intercom'
import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useCacheState } from '@Store/slices/cache'

/**
 * Boots Intercom with user identity to properly track events.
 * Required because anonymous events don't appear in dashboard.
 * We have to boot manually because App is running under iframe
 */
function IntercomBooter() {
  const { boot } = useIntercom()
  const { me } = useCacheState()

  useEffect(() => {
    if (me && typeof window.Intercom === 'function') {
      boot({
        userId: me.id,
        email: me.email,
        name: me.name,
      })
    }
  }, [me, boot])

  return null
}

/**
 * Tracks page navigations in Intercom.
 * Required because Intercom can't auto-detect route changes in Shopify iframe context.
 */
function IntercomUpdater() {
  const { update, trackEvent, boot } = useIntercom()
  const location = useLocation()

  // Verify Intercom loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof window.Intercom !== 'function') {
        boot()
      }
    }, 3000)

    return () => clearTimeout(timer)
  }, [boot])

  // Track route changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof window.Intercom === 'function') {
        update()
        trackEvent(`shopify_app_page_viewed_${location.pathname}`)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [location.pathname, update, trackEvent])

  return null
}

/**
 * Provides Intercom context with manual boot and route tracking for Shopify iframe environment.
 * IntercomBooter: boot with usr data
 * IntercomUpdater: track page views
 */
function IntercomProvider({
  children,
}: {
  children: JSX.Element
}): JSX.Element {
  return (
    <LibIntercomProvider
      appId={import.meta.env.VITE_INTERCOM_APP_ID}
      autoBoot={false}
      shouldInitialize={true}
    >
      <IntercomBooter />
      <IntercomUpdater />
      {children}
    </LibIntercomProvider>
  )
}

export default IntercomProvider
